import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';

import CustomTextField from '@/components/ui/CustomTextField/CustomTextField';
import FileField from '@/components/ui/FileField/FileField';
import { TAgent } from '@/types/agents';

import { EInputFieldType } from './types';
import useInputStage from './useInputStage';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

interface InputStageProps {
  agent: TAgent;
  inputValues: Record<string, string>;
  files: Record<string, File | File[]>;
  onInputValueChange: (key: string, value: string) => void;
  onFileChange: (key: string, file: File | File[] | null) => void;
  onRunTest: () => void;
}

const InputStage = ({
  agent,
  inputValues,
  files,
  onInputValueChange,
  onFileChange,
  onRunTest,
}: InputStageProps) => {
  const {
    isRunButtonDisabled,
    inputFieldsSchema,
    loadingFieldKey,
    handleFileChange,
  } = useInputStage({
    agent,
    inputValues,
    files,
    onInputValueChange,
    onFileChange,
  });

  return (
    <VStack align="stretch" gap={6}>
      <Box>
        <Text fontSize={18} fontWeight={500} color="fg" mb={3}>
          Prompt instructions
        </Text>
        {inputFieldsSchema.map(([key, value]) => {
          if (value.type === EInputFieldType.STRING) {
            return (
              <CustomTextField
                key={key}
                label={key.charAt(0).toUpperCase() + key.slice(1)}
                value={inputValues[key] || ''}
                onChange={(newValue) => onInputValueChange(key, newValue)}
                placeholder="Type some text for this prompt"
              />
            );
          }

          if (value.type === EInputFieldType.FILE || value.type === EInputFieldType.IMAGE || value.type === EInputFieldType.FILES) {
            return (
              <FileField
                key={key}
                label={key.charAt(0).toUpperCase() + key.slice(1)}
                value={files[key] || null}
                onChange={(file) => handleFileChange(key, file)}
                maxSize={MAX_FILE_SIZE}
                loading={loadingFieldKey === key}
                multiple={value.type === EInputFieldType.FILES}
              />
            );
          }

          return null;
        })}
      </Box>

      <HStack gap={3} width="100%" justify="flex-end" mt={4}>
        <Button
          variant="solid"
          colorPalette="purple"
          backgroundColor="purple.600"
          color="white"
          borderRadius={6}
          px={4}
          py={2}
          onClick={onRunTest}
          disabled={isRunButtonDisabled}
          _hover={{ backgroundColor: 'purple.700' }}
        >
          Run Test
        </Button>
      </HStack>
    </VStack>
  );
};

export default InputStage;

